import * as THREE from 'three';
import { createArena } from './Arena.js';
import { Player } from './Player.js';
import { Projectile } from './Projectile.js';
import { Effects } from './Effects.js';
import { InputLocal } from './InputLocal.js';
import { TouchInput } from './TouchInput.js';
import { NetworkClient } from './NetworkClient.js';
import { HUD } from './HUD.js';
import * as Audio from './Audio.js';
import {
  MAX_HP, ROUNDS_TO_WIN,
  LOCAL_CAM_HEIGHT,
  TPS_DISTANCE, TPS_HEIGHT, FPS_EYE_HEIGHT,
  ROUND_START_COUNTDOWN, RESPAWN_DELAY, NET_TICK_RATE,
  KEY_CAM_FPS, KEY_CAM_TPS, ARENA_RADIUS,
  CAM_SHAKE_DECAY,
  ZONE_START_DELAY, ZONE_SHRINK_RATE, ZONE_MIN_RADIUS,
  ZONE_DAMAGE_PER_SEC, ZONE_WARNING_TIME,
  PLAYER_RADIUS
} from './constants.js';

/* ============================================================
   Game — core engine
   • Local mode  → fixed top-down aerial view
   • Online mode → FPS (default) with V/B to toggle TPS
   ============================================================ */

export class Game {
  constructor() {
    this.mode = null;
    this.running = false;

    // ── Renderer ────────────────────────────
    this.canvas = document.getElementById('game-canvas');
    // Detect low-end: if deviceMemory API available and < 4GB, or mobile
    const isLowEnd = (navigator.deviceMemory && navigator.deviceMemory < 4)
      || /Android|iPhone|iPad/i.test(navigator.userAgent);
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: !isLowEnd,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isLowEnd ? 1 : 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = isLowEnd ? THREE.BasicShadowMap : THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      55, window.innerWidth / window.innerHeight, 0.1, 350
    );

    // ── Subsystems ──────────────────────────
    this.hud = new HUD();
    this.effects = new Effects(this.scene);
    this.input = null;
    this.touchInput = null;
    this.network = null;

    // ── Mobile detection ────────────────────
    this.isMobile = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    // ── State ───────────────────────────────
    this.players = [];
    this.projectiles = [];
    this.gameState = 'idle';

    // ── Timing ──────────────────────────────
    this.clock = new THREE.Clock();
    this.countdownTimer = 0;
    this.roundEndTimer = 0;
    this.netTickTimer = 0;

    // ── Camera state ────────────────────────
    this.cameraAngle = 0;          // menu orbit
    this.cameraMode = 'topdown';   // 'topdown' | 'fps' | 'tps'
    this.localPlayerId = 0;
    this.remotePlayerId = 1;

    // Mouse look (online FPS/TPS)
    this.mouseYaw = 0;
    this.mousePitch = 0;
    this.pointerLocked = false;

    // Camera shake
    this.shakeIntensity = 0;
    this.shakeOffset = new THREE.Vector3();

    // ── Zone (closing circle) ────────────────
    this.zoneRadius = ARENA_RADIUS;
    this.zoneTargetRadius = ARENA_RADIUS;
    this.zoneTimer = 0;             // time elapsed in round
    this.zoneShrinking = false;
    this.zoneWarned = false;
    this.zoneRing = null;           // 3D visual
    this.zoneWall = null;           // 3D wall visual

    this._setupScene();
    this._setupResize();
    this._setupMouseLook();
    this._setupZoneVisual();

    this._animate = this._animate.bind(this);
    requestAnimationFrame(this._animate);
  }

  // ═══════════════════════════════════════════════════════
  _setupScene() {
    this.scene.background = new THREE.Color(0x87CEEB);
    this.scene.fog = new THREE.Fog(0x87CEEB, 80, 200);

    this.scene.add(new THREE.AmbientLight(0xffe0c0, 0.6));

    const sun = new THREE.DirectionalLight(0xfff5e0, 1.4);
    sun.position.set(30, 55, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 120;
    sun.shadow.camera.left = sun.shadow.camera.bottom = -50;
    sun.shadow.camera.right = sun.shadow.camera.top = 50;
    sun.shadow.bias = -0.002;
    this.scene.add(sun);

    this.scene.add(new THREE.DirectionalLight(0x8ec8ff, 0.25).translateX(-20).translateY(30));
    this.scene.add(new THREE.HemisphereLight(0x87CEEB, 0xd4a574, 0.3));

    this.arena = createArena();
    this.scene.add(this.arena);

    this.camera.position.set(0, LOCAL_CAM_HEIGHT + 15, 50);
    this.camera.lookAt(0, 0, 0);
  }

  _setupResize() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  _setupMouseLook() {
    // Pointer-lock for online FPS / TPS
    this.canvas.addEventListener('click', () => {
      if (this.mode === 'online' && this.running && !this.pointerLocked) {
        this.canvas.requestPointerLock();
      }
    });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      this.mouseYaw -= e.movementX * 0.003;
      this.mousePitch -= e.movementY * 0.002;
      this.mousePitch = Math.max(-1.2, Math.min(1.2, this.mousePitch));
    });

    // V / B camera toggle in online mode
    window.addEventListener('keydown', (e) => {
      if (this.mode !== 'online' || !this.running) return;
      if (e.code === KEY_CAM_FPS) {
        this.cameraMode = 'fps';
        this.hud.showCamIndicator('fps');
      } else if (e.code === KEY_CAM_TPS) {
        this.cameraMode = 'tps';
        this.hud.showCamIndicator('tps');
      }
    });
  }

  // ═══════════════════════════════════════════════════════
  //  ZONE VISUAL
  // ═══════════════════════════════════════════════════════

  _setupZoneVisual() {
    // Ring on ground
    const ringGeo = new THREE.RingGeometry(ARENA_RADIUS - 0.3, ARENA_RADIUS + 0.3, 128);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xff2200, transparent: true, opacity: 0, side: THREE.DoubleSide,
    });
    this.zoneRing = new THREE.Mesh(ringGeo, ringMat);
    this.zoneRing.rotation.x = -Math.PI / 2;
    this.zoneRing.position.y = 0.05;
    this.scene.add(this.zoneRing);

    // Translucent wall cylinder
    const wallGeo = new THREE.CylinderGeometry(ARENA_RADIUS, ARENA_RADIUS, 15, 128, 1, true);
    const wallMat = new THREE.MeshBasicMaterial({
      color: 0xff1100, transparent: true, opacity: 0, side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.zoneWall = new THREE.Mesh(wallGeo, wallMat);
    this.zoneWall.position.y = 7.5;
    this.scene.add(this.zoneWall);
  }

  _updateZoneVisual() {
    const r = this.zoneRadius;
    const scale = r / ARENA_RADIUS;
    this.zoneRing.scale.set(scale, scale, 1);
    this.zoneWall.scale.set(scale, 1, scale);

    // Opacity — more visible as zone shrinks
    const shrinkFrac = 1 - (r / ARENA_RADIUS);
    const baseOpacity = shrinkFrac > 0.01 ? 0.15 + shrinkFrac * 0.3 : 0;
    this.zoneRing.material.opacity = baseOpacity + Math.sin(Date.now() * 0.004) * 0.05;
    this.zoneWall.material.opacity = baseOpacity * 0.4;

    // Pulsing color shift when actively shrinking
    if (this.zoneShrinking) {
      const pulse = Math.sin(Date.now() * 0.008) * 0.5 + 0.5;
      this.zoneRing.material.color.setRGB(1, pulse * 0.2, 0);
    }
  }

  /** Add camera screenshake */
  addShake(intensity) {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  // ═══════════════════════════════════════════════════════
  //  START MODES
  // ═══════════════════════════════════════════════════════

  async startOnlineGame(serverUrl, roomCode) {
    this.mode = 'online';
    this.cameraMode = this.isMobile ? 'topdown' : 'fps';
    this.input = new InputLocal();
    this.network = new NetworkClient();

    // Mobile: use touch controls for online too
    if (this.isMobile) {
      this.touchInput = new TouchInput();
      this.touchInput.show();
    }

    const statusEl = document.getElementById('lobby-status');
    try {
      statusEl.textContent = 'Connecting…';
      await this.network.connect(serverUrl, roomCode);
      statusEl.textContent = 'Connected! Waiting for room…';
    } catch (e) {
      statusEl.textContent = e.message || 'Failed to connect to server.';
      this.network.disconnect();
      return false;
    }

    return new Promise((resolve) => {
      this.network.onRoomJoined = (msg) => {
        statusEl.textContent = `Room: ${msg.roomId} — Player ${msg.playerId + 1}. Waiting for opponent…`;
      };
      this.network.onGameStart = () => {
        document.getElementById('online-lobby').classList.remove('active');
        document.getElementById('menu-screen').classList.add('hidden');

        this.localPlayerId = this.network.playerId;
        this.remotePlayerId = this.localPlayerId === 0 ? 1 : 0;

        this.players = [new Player(0, this.scene), new Player(1, this.scene)];

        this.hud.show();
        this.hud.setNames(
          this.localPlayerId === 0 ? 'YOU' : 'OPPONENT',
          this.localPlayerId === 1 ? 'YOU' : 'OPPONENT',
        );
        if (this.isMobile) {
          this.hud.setControlsHelp('Touch: Joystick to move | Tap buttons to attack');
        } else {
          this.hud.setControlsHelp('ZQSD move | X rock | C paper | V scissors | F fps / G tps');
        }
        if (!this.isMobile) this.hud.showCamIndicator('fps');
        this.hud.updateRoundDisplay(0, 0, ROUNDS_TO_WIN);

        this.mouseYaw = this.localPlayerId === 0 ? 0 : Math.PI;
        this.mousePitch = 0;

        this.running = true;
        Audio.startMusic();
        this._startRound();
        resolve(true);
      };
      this.network.onMessage = (msg) => this._handleNetworkMessage(msg);
      this.network.onOpponentDisconnect = () => {
        if (!this.running) return;
        this.hud.showMessage('OPPONENT DISCONNECTED', 3000, '#ff4444');
        setTimeout(() => this.returnToMenu(), 3000);
      };
      this.network.onDisconnect = () => {
        // Unexpected socket drop (not opponent leaving, not us leaving)
        if (!this.running) return;
        this.hud.showMessage('CONNECTION LOST', 3000, '#ff4444');
        setTimeout(() => this.returnToMenu(), 3000);
      };
    });
  }

  returnToMenu() {
    this.running = false;
    this.gameState = 'idle';
    Audio.stopMusic();
    for (const p of this.players) p.dispose();
    this.players = [];
    for (const proj of this.projectiles) proj.destroy();
    this.projectiles = [];
    this.effects.dispose();
    if (this.network) this.network.disconnect();
    this.network = null;
    if (this.touchInput) { this.touchInput.hide(); }
    this.hud.hide();
    this.hud.hideMessage();
    this.hud.hideCamIndicator();
    if (document.pointerLockElement) document.exitPointerLock();
    document.getElementById('menu-screen').classList.remove('hidden');
    document.getElementById('online-lobby').classList.remove('active');
  }

  // ═══════════════════════════════════════════════════════
  //  ROUND MANAGEMENT
  // ═══════════════════════════════════════════════════════

  _startRound() {
    for (const p of this.players) p.reset();
    for (const proj of this.projectiles) proj.destroy();
    this.projectiles = [];
    this.gameState = 'countdown';
    this.countdownTimer = ROUND_START_COUNTDOWN;
    this._lastCountdownSec = -1;

    // Reset zone
    this.zoneRadius = ARENA_RADIUS;
    this.zoneTargetRadius = ARENA_RADIUS;
    this.zoneTimer = 0;
    this.zoneShrinking = false;
    this.zoneWarned = false;

    this.hud.updateHealth(MAX_HP, MAX_HP, MAX_HP);
    this.hud.showMessage('GET READY', 0);
    this.hud.hideZoneWarning();
  }

  _onRoundEnd(loserId) {
    this.gameState = 'round-end';
    this.roundEndTimer = RESPAWN_DELAY;
    const winner = loserId === 0 ? this.players[1] : this.players[0];
    winner.wins++;
    this.hud.updateRoundDisplay(this.players[0].wins, this.players[1].wins, ROUNDS_TO_WIN);

    if (winner.wins >= ROUNDS_TO_WIN) {
      this.gameState = 'match-end';
      const label = winner.id === this.localPlayerId ? 'YOU WIN!' : 'YOU LOSE!';
      this.hud.showMessage(label, 0, '#FFD700');
      Audio.victory();
      setTimeout(() => this.returnToMenu(), 4000);
    } else {
      this.hud.showMessage(
        `PLAYER ${winner.id + 1} WINS ROUND!`,
        RESPAWN_DELAY * 1000,
        winner.id === 0 ? '#64B5F6' : '#EF5350'
      );
    }
  }

  // ═══════════════════════════════════════════════════════
  //  MAIN LOOP
  // ═══════════════════════════════════════════════════════

  _animate() {
    requestAnimationFrame(this._animate);
    const dt = Math.min(this.clock.getDelta(), 0.05);
    if (this.running) this._update(dt);
    this._updateCamera(dt);

    // Camera shake
    if (this.shakeIntensity > 0.01) {
      this.shakeOffset.set(
        (Math.random() - 0.5) * this.shakeIntensity,
        (Math.random() - 0.5) * this.shakeIntensity * 0.6,
        (Math.random() - 0.5) * this.shakeIntensity * 0.4
      );
      this.camera.position.add(this.shakeOffset);
      this.shakeIntensity *= Math.max(0, 1 - CAM_SHAKE_DECAY * dt);
    }

    // Update zone visual
    if (this.running) this._updateZoneVisual();

    this.renderer.render(this.scene, this.camera);
  }

  _update(dt) {
    if (this.input) this.input.update();
    switch (this.gameState) {
      case 'countdown': this._updateCountdown(dt); break;
      case 'playing':   this._updatePlaying(dt);   break;
      case 'round-end': this._updateRoundEnd(dt);  break;
    }
    this.effects.update(dt);
  }

  _updateCountdown(dt) {
    this.countdownTimer -= dt;
    const sec = Math.ceil(this.countdownTimer);
    if (sec > 0) {
      this.hud.showMessage(sec.toString(), 0, '#FFD700');
      if (sec !== this._lastCountdownSec) { Audio.countdown(); this._lastCountdownSec = sec; }
    } else {
      this.hud.showMessage('FIGHT!', 800, '#FF4444');
      Audio.fight();
      this.gameState = 'playing';
    }
  }

  _updateRoundEnd(dt) {
    this.roundEndTimer -= dt;
    if (this.roundEndTimer <= 0 && this.gameState === 'round-end') this._startRound();
  }

  // ═══════════════════════════════════════════════════════
  //  GAMEPLAY UPDATE
  // ═══════════════════════════════════════════════════════

  _updatePlaying(dt) {
    // ── Zone timer & shrink ──
    this.zoneTimer += dt;
    if (this.zoneTimer > ZONE_START_DELAY && this.zoneTargetRadius > ZONE_MIN_RADIUS) {
      if (!this.zoneWarned && this.zoneTimer > ZONE_START_DELAY - ZONE_WARNING_TIME) {
        this.zoneWarned = true;
        this.hud.showZoneWarning();
        Audio.zoneWarning();
      }
      this.zoneShrinking = true;
      this.zoneTargetRadius = Math.max(
        ZONE_MIN_RADIUS,
        ARENA_RADIUS - (this.zoneTimer - ZONE_START_DELAY) * ZONE_SHRINK_RATE
      );
    }

    // Smooth shrink
    if (this.zoneRadius > this.zoneTargetRadius) {
      this.zoneRadius = Math.max(this.zoneTargetRadius, this.zoneRadius - ZONE_SHRINK_RATE * dt);
    }

    // Zone damage
    for (const p of this.players) {
      if (!p.alive) continue;
      const dist = Math.sqrt(p.position.x * p.position.x + p.position.z * p.position.z);
      if (dist > this.zoneRadius - PLAYER_RADIUS) {
        const dmg = ZONE_DAMAGE_PER_SEC * dt;
        p.hp = Math.max(0, p.hp - dmg);
        if (p.hp <= 0) p.alive = false;
        // Red vignette pulse
        if (Math.random() < dt * 3) this.effects.zoneDamageEffect(p.position);
      }
    }

    if (this.mode === 'online') this._updateOnline(dt);

    // ── Projectile logic ──
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];
      proj.update(dt);
      if (!proj.alive) { this.projectiles.splice(i, 1); continue; }

      for (const player of this.players) {
        if (!proj.checkCollision(player)) continue;

        if (player.isBlocking) {
          // Paper blocks rock — but counter: brief stun on blocker
          this.effects.shieldBlockEffect(player.position);
          Audio.block();
          player.staggerTimer = Math.max(player.staggerTimer, 0.2);
          proj.destroy();
          this.addShake(0.4);
        } else {
          // Rock hits
          const fromDir = new THREE.Vector3().subVectors(player.position, proj.position).normalize();

          // Counter: rock vs scissors — disable scissors for 3 seconds
          if (player.currentWeapon === 'scissors') {
            player.scissorsCooldown = Math.max(player.scissorsCooldown, 3.0);
            player.currentWeapon = null;
            player._updateWeaponVisuals();
          }

          if (player.takeDamage(proj.damage, fromDir, 'rock')) {
            this.effects.rockHitEffect(player.position);
            this.effects.dustPuff(player.position);
            Audio.hit();
            this.addShake(0.8);
          }
          proj.destroy();
        }
        this.projectiles.splice(i, 1);
        break;
      }
    }

    // HUD
    this.hud.updateHealth(this.players[0].hp, this.players[1].hp, MAX_HP);
    this.hud.updateZoneTimer(this.zoneTimer, ZONE_START_DELAY, this.zoneRadius, ZONE_MIN_RADIUS);
    for (const p of this.players) {
      this.hud.updateWeapons(p.id, p.currentWeapon, {
        rock: p.rockCooldown, scissors: p.scissorsCooldown, paper: p.paperCooldown,
      });
    }

    // Round end check
    for (const p of this.players) {
      if (!p.alive) { Audio.death(); this.addShake(1.5); this._onRoundEnd(p.id); break; }
    }
  }

  // ── ONLINE ──
  _updateOnline(dt) {
    const local = this.players[this.localPlayerId];
    const remote = this.players[this.remotePlayerId];

    let a;
    if (this.isMobile && this.touchInput) {
      this.touchInput.update();
      a = this.touchInput.getActions(0);
    } else {
      a = this.input.getActions(0);
    }

    // Movement (relative to mouse-look yaw)
    if (this.cameraMode !== 'topdown') {
      const sin = Math.sin(this.mouseYaw);
      const cos = Math.cos(this.mouseYaw);
      const fx = a.moveX * cos + a.moveY * sin;
      const fz = -a.moveX * sin + a.moveY * cos;
      local.moveInput.set(fx, fz);
      // Face direction = where camera looks (horizontal)
      local.facing.set(-sin, 0, -cos).normalize();
    } else {
      local.moveInput.set(a.moveX, a.moveY);
    }

    // Dash
    if (a.dash) {
      if (local.doDash()) {
        Audio.dash();
        this.network.sendAction({ type: 'dash' });
      }
    }

    // Rock
    if (a.rock) {
      const proj = local.useRock();
      if (proj) {
        // Override direction to camera look dir
        if (this.cameraMode !== 'topdown') {
          proj.direction.set(-Math.sin(this.mouseYaw), 0, -Math.cos(this.mouseYaw)).normalize();
        }
        this.projectiles.push(new Projectile(this.scene, proj.origin, proj.direction, proj.owner));
        Audio.rockThrow();
        this.network.sendAction({
          type: 'rock',
          origin: { x: proj.origin.x, y: proj.origin.y, z: proj.origin.z },
          direction: { x: proj.direction.x, y: proj.direction.y, z: proj.direction.z },
        });
      }
    }

    // Paper
    if (a.paperHold) { if (!local.isBlocking) local.startBlocking(); }
    else if (local.isBlocking) local.stopBlocking();

    // Scissors
    if (a.scissors) {
      Audio.scissorsSwing();
      const hit = this._doScissorsAttack(local, remote);
      if (hit) {
        this.network.sendAction({ type: 'scissors-hit', damage: hit.damage });
      } else {
        this.network.sendAction({ type: 'scissors-miss' });
      }
    }

    local.update(dt, this.zoneRadius);
    remote.update(dt, this.zoneRadius);

    // Send state
    this.netTickTimer += dt;
    if (this.netTickTimer >= 1 / NET_TICK_RATE) {
      this.netTickTimer = 0;
      this.network.sendState(local.getState());
    }
  }

  // ── Scissors melee helper (shared logic) ──
  _doScissorsAttack(attacker, defender) {
    const result = attacker.useScissors(defender);
    if (result) {
      const fromDir = new THREE.Vector3().subVectors(defender.position, attacker.position).normalize();
      const hit = defender.takeDamage(result.damage, fromDir, 'scissors');
      if (hit) {
        this.effects.scissorsSlashEffect(attacker.position, attacker.facing);
        if (result.crit) {
          this.effects.scissorsVsPaperEffect(defender.position);
          Audio.scissorsCrit();
          this.addShake(1.2);
        } else {
          this.effects.rockHitEffect(defender.position);
          Audio.hit();
          this.addShake(0.6);
        }
        this.effects.dustPuff(defender.position);
      }
      return result;
    } else {
      // Swing miss
      this.effects.scissorsSlashEffect(attacker.position, attacker.facing);
      return null;
    }
  }

  // ── Network messages ──
  _handleNetworkMessage(msg) {
    if (!this.running) return;
    switch (msg.type) {
      case 'player-state': {
        const remote = this.players[this.remotePlayerId];
        if (remote && msg.state) remote.applyState(msg.state);
        break;
      }
      case 'player-action': {
        const remote = this.players[this.remotePlayerId];
        const local = this.players[this.localPlayerId];
        const act = msg.action;
        if (act.type === 'rock') {
          const o = new THREE.Vector3(act.origin.x, act.origin.y, act.origin.z);
          const d = new THREE.Vector3(act.direction.x, act.direction.y, act.direction.z);
          this.projectiles.push(new Projectile(this.scene, o, d, this.remotePlayerId));
          remote.attackAnim = 1.0;
          Audio.rockThrow();
        } else if (act.type === 'scissors-hit') {
          const fromDir = new THREE.Vector3().subVectors(local.position, remote.position).normalize();
          local.takeDamage(act.damage, fromDir, 'scissors');
          this.effects.scissorsSlashEffect(remote.position, remote.facing);
          Audio.scissorsSwing();
          if (local.isBlocking) {
            this.effects.scissorsVsPaperEffect(local.position);
            Audio.scissorsCrit();
            local.stopBlocking();
          } else {
            this.effects.rockHitEffect(local.position);
            Audio.hit();
          }
          this.effects.dustPuff(local.position);
        } else if (act.type === 'scissors-miss') {
          this.effects.scissorsSlashEffect(remote.position, remote.facing);
          Audio.scissorsSwing();
        }
        break;
      }
      case 'game-event': {
        if (msg.event.type === 'round-end') this._onRoundEnd(msg.event.loserId);
        break;
      }
    }
  }

  // ═══════════════════════════════════════════════════════
  //  CAMERA
  // ═══════════════════════════════════════════════════════

  _updateCamera(dt) {
    // Menu orbit
    if (!this.running || this.players.length < 2) {
      this.cameraAngle += dt * 0.12;
      const r = 65;
      this.camera.position.set(Math.sin(this.cameraAngle) * r, 50, Math.cos(this.cameraAngle) * r);
      this.camera.lookAt(0, 5, 0);
      return;
    }

    switch (this.cameraMode) {
      case 'topdown': this._camTopDown(dt); break;
      case 'fps':     this._camFPS(dt); break;
      case 'tps':     this._camTPS(dt); break;
    }
  }

  /** Local mode — fixed static top-down camera, no movement */
  _camTopDown(dt) {
    this.camera.position.set(0, LOCAL_CAM_HEIGHT, 0.01);
    this.camera.lookAt(0, 0, 0);
    this.camera.fov = 55;
    this.camera.updateProjectionMatrix();
  }

  /** Online FPS — camera at player's eye level, mouse controlled */
  _camFPS(dt) {
    const local = this.players[this.localPlayerId];
    if (!local) return;

    const eyePos = local.position.clone();
    eyePos.y += FPS_EYE_HEIGHT;

    // Very slight head bob
    const speed = Math.sqrt(local.velocity.x * local.velocity.x + local.velocity.z * local.velocity.z);
    if (speed > 2) {
      eyePos.y += Math.sin(local.walkCycle * 1.1) * 0.06;
      eyePos.x += Math.cos(local.walkCycle * 0.55) * 0.03;
    }

    // Look direction from mouse yaw/pitch
    const dir = new THREE.Vector3(
      -Math.sin(this.mouseYaw) * Math.cos(this.mousePitch),
      Math.sin(this.mousePitch),
      -Math.cos(this.mouseYaw) * Math.cos(this.mousePitch)
    );

    this.camera.position.copy(eyePos);
    this.camera.lookAt(eyePos.clone().add(dir));
    this.camera.fov = 85;
    this.camera.updateProjectionMatrix();

    // Hide local player mesh in FPS
    local.group.visible = false;
  }

  /** Online TPS — smooth follow camera behind & above the local player */
  _camTPS(dt) {
    const local = this.players[this.localPlayerId];
    if (!local) return;

    // Show player in TPS
    if (local.hitFlashTimer <= 0) local.group.visible = true;

    const dir = new THREE.Vector3(
      -Math.sin(this.mouseYaw),
      0,
      -Math.cos(this.mouseYaw)
    );

    const idealPos = local.position.clone()
      .add(dir.clone().multiplyScalar(-TPS_DISTANCE))
      .add(new THREE.Vector3(0, TPS_HEIGHT, 0));

    // Subtle offset to the right for over-shoulder feel
    const rightDir = new THREE.Vector3(dir.z, 0, -dir.x);
    idealPos.addScaledVector(rightDir, 1.2);

    // ── Hard clamp: camera MUST stay well inside the arena ──
    const maxCamR = ARENA_RADIUS - 4;
    const distXZ = Math.sqrt(idealPos.x * idealPos.x + idealPos.z * idealPos.z);
    if (distXZ > maxCamR) {
      const scale = maxCamR / distXZ;
      idealPos.x *= scale;
      idealPos.z *= scale;
    }
    idealPos.y = Math.min(idealPos.y, 18);

    // Smooth but responsive follow
    const lerpSpeed = Math.min(dt * 14, 1);
    this.camera.position.lerp(idealPos, lerpSpeed);

    // Final hard clamp AFTER lerp
    const finalDistXZ = Math.sqrt(
      this.camera.position.x * this.camera.position.x +
      this.camera.position.z * this.camera.position.z
    );
    if (finalDistXZ > maxCamR) {
      const s = maxCamR / finalDistXZ;
      this.camera.position.x *= s;
      this.camera.position.z *= s;
    }

    const lookTarget = local.position.clone()
      .add(new THREE.Vector3(0, 2.5, 0))
      .add(dir.clone().multiplyScalar(5));
    this.camera.lookAt(lookTarget);
    this.camera.fov = 65;
    this.camera.updateProjectionMatrix();
  }
}
